import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { getEvents } from '@redux/features/eventsSlice';
import { formatDateToEUFormat } from 'helpers/formatDateToEUFormat';
import { clearArrayFromNulls } from 'helpers/clearArrayFromNulls';
import { getDanceStyleFromPath } from 'helpers/danceStyleUtils';
import {
  getEventCover,
  getEventCountry,
  getEventEntryForm,
  getEventOrganizationName,
  isEphanEvent,
} from 'helpers/eventUtils';
import { formatDate } from 'helpers/formatDate';
import { useEphanLive } from 'hooks/useEphanLive';
import { pathnames } from 'constants/pathnames';
import { EVENT_BANNER_RATIO } from 'constants/imageRatios';
import { ImageComponent } from 'components/Image';
import { Container } from 'components/Container';
import { Button } from 'components/Button';
import { Link } from 'components/Link';
import { Seo } from 'components/Seo';
import { Information } from 'pages/Calendar/EventInfo/tabs/Information';
import { Categories } from 'pages/Calendar/EventInfo/tabs/Categories';
import { EphanCategories } from 'pages/Calendar/EventInfo/tabs/EphanCategories';
import { Address } from 'pages/Calendar/EventInfo/tabs/Address';
import { Judges } from 'pages/Calendar/EventInfo/tabs/Judges';
import { Tabs } from 'components/Tabs';
import { NotFound } from 'pages/Services/NotFound';
import calendarIcon from 'assets/icons/calendar.svg';
import markerIcon from 'assets/icons/marker.svg';
import styles from 'pages/Calendar/EventInfo/EventInfo.scss';

export const EventInfo = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tabs, setTabs] = useState({});
  const modalRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalOpen, setIsModalOpen]);

  const [registrationStatus, setRegistrationStatus] = useState('hidden');
  const { isLoading, events } = useSelector((state) => state.events);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [nextEventSlug, setNextEventSlug] = useState('');
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { slug } = useParams();

  const pathnameParts = pathname.split('/');
  const currentDanceStyleCalendarLink = `/${clearArrayFromNulls(pathnameParts)[0]}${pathnames.calendarPage}`;
  const today = new Date().setHours(0, 0, 0, 0);

  useEffect(() => {
    const danceStyle = getDanceStyleFromPath(pathname);
    if (danceStyle && !events.length) {
      dispatch(getEvents({ danceStyle }));
    }
  }, []);

  useEffect(() => {
    setCurrentEvent(null);
    if (events.length) {
      const currentEvent = events.find((event) => event.slug === slug);
      setCurrentEvent(currentEvent);

      const currentEventIndex = events.indexOf(currentEvent);
      const nextEvent = events[currentEventIndex + 1];

      setNextEventSlug(nextEvent?.slug ?? '');

      if (currentEvent) {
        const { startDate, endDate, registration } = currentEvent;
        const registrationStart = new Date(registration?.startDate).setHours(0, 0, 0, 0);
        const registrationEnd = new Date(registration?.endDate).setHours(0, 0, 0, 0);
        const eventStartDate = new Date(startDate).setHours(0, 0, 0, 0);
        const eventEndDate = new Date(endDate).setHours(0, 0, 0, 0);
        const isOver = today > eventEndDate && today > eventStartDate;

        if (isEphanEvent(currentEvent)) {
          // e-phan states plainly whether it is taking entries, and it may do so
          // without publishing a deadline. Its answer wins over date arithmetic.
          if (isOver || typeof registration?.accept !== 'boolean') {
            setRegistrationStatus('hidden');
          } else {
            setRegistrationStatus(registration.accept ? 'open' : 'closed');
          }
        } else if (today < registrationStart) {
          setRegistrationStatus('hidden');
        } else if (today >= registrationStart && today <= registrationEnd) {
          setRegistrationStatus('open');
        } else if (today > registrationEnd && !isOver) {
          setRegistrationStatus('closed');
        } else {
          setRegistrationStatus('hidden');
        }
      }
    }
  }, [events, pathname]);

  const {
    type,
    title,
    organization,
    startDate,
    endDate,
    cover,
    city,
    registration,
    description,
    information,
    departments,
    ephanCategories,
    judges,
    address,
    resultsUrl,
    ephanWebUrl,
  } = currentEvent || {};

  // Entry counts move all day, so they come straight from e-phan rather than
  // from whatever the last sync happened to store.
  const { entriesCount, maxEntries } = useEphanLive(currentEvent);

  useEffect(() => {
    if (description || information) {
      setTabs((prevTabs) => ({ Information, ...prevTabs }));
    }
    if (departments?.length) {
      setTabs((prevTabs) => ({ ...prevTabs, Categories }));
    }
    if (ephanCategories?.length) {
      setTabs((prevTabs) => ({ ...prevTabs, Categories: EphanCategories }));
    }
    if (judges?.length) {
      setTabs((prevTabs) => ({ ...prevTabs, Judges }));
    }
    if (address) {
      setTabs((prevTabs) => ({ ...prevTabs, Address }));
    }
  }, [currentEvent]);

  return currentEvent ? (
    <Container className={styles.page}>
      <Seo
        title={title}
        description={description || `${title} — ${type} in ${[city, getEventCountry(currentEvent)].filter(Boolean).join(', ')}, organized by ECDS.`}
        image={getEventCover(currentEvent)}
        type='article'
      />
      <nav className={styles.navigation}>
        <Link content='All events' path={currentDanceStyleCalendarLink} arrowLeft />
        {nextEventSlug && <Link content='Next' path={`${currentDanceStyleCalendarLink}/${nextEventSlug}`} arrowRight />}
      </nav>
      <article className={styles.info}>
        <ImageComponent
          src={getEventCover(currentEvent) || 'https://placehold.co/282x353'}
          alt={cover?.alternativeText || 'cover placeholder'}
          placeholder={cover?.placeholder}
          className={styles.cover}
          fit='contain'
          ratio={EVENT_BANNER_RATIO}
        />
        <div className={styles.information}>
          <div className={styles.stats}>
            <span className={styles.type}>{type}</span>
            {registrationStatus !== 'hidden' && (
              <span className={cn(styles.status, styles[registrationStatus])}>
                {`Registration is ${registrationStatus}`}
              </span>
            )}
          </div>
          <h2 className={styles.title}>{title}</h2>
          <div className={styles.additionalInfo}>
            <span>
              <img src={calendarIcon} alt='calendar' />
              {formatDate(startDate, endDate)}
            </span>
            <span>
              <img src={markerIcon} alt='marker' />
              {[city, getEventCountry(currentEvent)].filter(Boolean).join(', ')}
            </span>
          </div>
          <ImageComponent
            src={getEventCover(currentEvent) || 'https://placehold.co/282x353'}
            alt={cover?.alternativeText || 'cover placeholder'}
            placeholder={cover?.placeholder}
            className={cn(styles.cover, styles.coverMobile)}
            fit='contain'
            ratio={EVENT_BANNER_RATIO}
          />
          <table className={styles.table}>
            <tbody>
              {registration?.endDate && (
                <tr>
                  <td>Registration to</td>
                  <td>{formatDateToEUFormat(registration?.endDate)}</td>
                </tr>
              )}
              {getEventOrganizationName(currentEvent) && (
                <tr>
                  <td>Organization</td>
                  <td>
                    {organization?.website ? (
                      <Link
                        content={getEventOrganizationName(currentEvent)}
                        path={`https://${organization.website}`}
                        external
                        hoverStyle
                        noStyle
                      />
                    ) : (
                      getEventOrganizationName(currentEvent)
                    )}
                  </td>
                </tr>
              )}
              {Number.isFinite(entriesCount) && registration?.accept && (
                <tr>
                  <td>Entries</td>
                  <td>{maxEntries ? `${entriesCount} / ${maxEntries}` : entriesCount}</td>
                </tr>
              )}
            </tbody>
          </table>
          <div className={styles.buttons}>
            {today <= new Date(endDate).setHours(0, 0, 0, 0) && (
              <Button
                text='Registration'
                className={styles.firstBtn}
                onClick={() =>
                  title === 'ECDS European Championship'
                    ? setIsModalOpen(true)
                    : window.open(registration?.url ?? ephanWebUrl)
                }
                disabled={registrationStatus !== 'open'}
                normalStyle
              />
            )}
            <Button
              text='Entry form'
              className={styles.secondBtn}
              onClick={() => window.open(getEventEntryForm(currentEvent))}
              disabled={!getEventEntryForm(currentEvent)}
              ghostStyle
            />
            {resultsUrl && (
              <Button text='Results' className={styles.firstBtn} onClick={() => window.open(resultsUrl)} normalStyle />
            )}
          </div>
          <Tabs className={styles.tabs} tabs={tabs} setTabs={setTabs} data={currentEvent} />
        </div>
      </article>
      <Tabs className={cn(styles.tabs, styles.tabsMobile)} tabs={tabs} setTabs={setTabs} data={currentEvent} />
      <div className={cn(styles.overlay, { [styles.active]: isModalOpen })}></div>
      <div className={cn(styles.modal, { [styles.active]: isModalOpen })} ref={modalRef}>
        <p>
          To apply for participation in the European Championship, you must register in the e-phan database. The
          application must be confirmed by one of the{' '}
          <Link
            className={styles.modalLink}
            path='https://euro-dance.org/members'
            content='national organizations'
            hoverStyle
            noStyle
            external
          />
          .
        </p>
        <p>
          If the dancer is from a country that is not on{' '}
          <Link path='https://euro-dance.org/members' content='this list' hoverStyle noStyle external />, then his
          application will be confirmed by the Committee of Independent Dancers of the ECDS.
        </p>
        <p>
          If your club and coach are already registered in e-phan database, you will be able to select them during
          registration. If not, ask them to do so before your registration.
        </p>
        <Button
          className={styles.modalBtn}
          text='Continue to registration'
          onClick={() => {
            window.open(registration?.url);
            setIsModalOpen(false);
          }}
          normalStyle
        />
      </div>
    </Container>
  ) : isLoading ? (
    <p>Loading...</p>
  ) : (
    <NotFound />
  );
};
